import React, { useState, useEffect } from 'react';
import { 
  Typography, Paper, Box, Stepper, Step, StepLabel, StepContent,
  Button, CircularProgress, Alert, Chip, Divider
} from '@mui/material';
import api from '../utils/api';

// Status color mapping
const getStatusColor = (status) => {
  switch(status) {
    case 'initial': return 'info';
    case 'in_progress': return 'warning';
    case 'waiting': return 'secondary';
    case 'returned': return 'primary';
    case 'completed': return 'success';
    default: return 'default';
  }
};

// Status label mapping
const getStatusLabel = (status) => {
  switch(status) {
    case 'initial': return 'Aloitettu';
    case 'in_progress': return 'Käsittelyssä';
    case 'waiting': return 'Odottaa';
    case 'returned': return 'Palautunut';
    case 'completed': return 'Valmis';
    default: return status || 'Tuntematon';
  }
};

const ReturnStages = ({ returnId }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('fi-FI');
  };

  // Function to fetch stages
  const fetchStages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/returns/${returnId}/stages`);
      const fetchedStages = response.data;
      setStages(fetchedStages);
      
      // Set the active step to the last stage (most recent)
      if (fetchedStages.length > 0) {
        setActiveStep(fetchedStages.length - 1);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching return stages:', err);
      setError('Virhe palautuksen vaiheiden haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch return stages on mount and when returnId changes
  useEffect(() => {
    if (returnId) {
      fetchStages();
    }
  }, [returnId]);
  
  // Listen for stageAdded event to refresh stages
  useEffect(() => {
    const handleStageAdded = () => {
      if (returnId) {
        fetchStages();
      }
    };
    
    window.addEventListener('stageAdded', handleStageAdded);
    
    return () => {
      window.removeEventListener('stageAdded', handleStageAdded);
    };
  }, [returnId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (stages.length === 0) {
    return (
      <Paper sx={{ p: 2, my: 2 }}>
        <Typography variant="body1">
          Ei vaiheita tälle palautukselle.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, my: 2 }}>
      <Typography variant="h6" gutterBottom>
        Palautuksen vaiheet
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Stepper orientation="vertical" activeStep={activeStep}>
        {stages.map((stage, index) => (
          <Step key={stage.id} expanded>
            <StepLabel 
              optional={
                <Typography variant="caption">
                  {formatDate(stage.stage_date)}
                </Typography>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Description removed as requested */}
                <Chip 
                  size="small" 
                  label={getStatusLabel(stage.status)} 
                  color={getStatusColor(stage.status)} 
                />
              </Box>
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                {stage.handler && (
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Käsittelijä: {stage.handler}
                  </Typography>
                )}
                {stage.notes && (
                  <Typography variant="body2">
                    {stage.notes}
                  </Typography>
                )}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Paper>
  );
};

export default ReturnStages;
